// Timeshare worksheet helpers (pure). A worksheet records the hours of each calendar day of `year` that the
// child spends with `parent`; the child's NCP timeshare is derived from it as HOUR per YEAR when attached.
import { UNIT_MONTHS, otherParty, type Child, type PartyIndex, type TimeshareMode, type TimeshareWorksheet } from '@divorceos/schema';

export const HOURS_PER_DAY = 24;
export const DAYS_PER_WEEK = 7;
/** Santa Clara mode day values: none, quarter, half, three-quarter, all. */
export const SC_STEPS: readonly number[] = [0, 6, 12, 18, 24];
export const SC_LABEL: Record<number, string> = { 0: '', 6: '¼', 12: '½', 18: '¾', 24: 'All' };
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));
/** Monday-first weekday names (the original's calendar is Monday-first). */
export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export type PatternMode = 'YEAR' | 'WEEK1' | 'WEEK2';
export const PATTERN_MODE_LABEL: Record<PatternMode, string> = { YEAR: 'Year', WEEK1: 'Alt Week 1', WEEK2: 'Alt Week 2' };

const PATTERN_LENGTH = DAYS_PER_WEEK * HOURS_PER_DAY;
const zeros = (n: number): number[] => new Array<number>(n).fill(0);

export const isLeapYear = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
export const daysInYear = (year: number): number => (isLeapYear(year) ? 366 : 365);
export const daysInMonth = (year: number, month: number): number => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

/** 0-based day of year for (year, month 0..11, day 1..31). */
export function dayOfYear(year: number, month: number, day: number): number {
  const start = Date.UTC(year, 0, 1);
  return Math.round((Date.UTC(year, month, day) - start) / 86_400_000);
}

/** Monday-first weekday (0 = Monday .. 6 = Sunday) of a date. */
export const mondayWeekday = (d: Date): number => (d.getUTCDay() + 6) % 7;

/** ISO-8601 week number of a UTC date. */
export function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = t.getUTCDay() || 7; // Mon=1..Sun=7
  t.setUTCDate(t.getUTCDate() + 4 - dayNum); // Thursday of this week
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  return Math.ceil(((t.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

export function emptyWorksheet(year: number, mode: TimeshareMode, parent: PartyIndex): TimeshareWorksheet {
  return { year, mode, parent, pattern: { alternate: false, week1: zeros(PATTERN_LENGTH), week2: zeros(PATTERN_LENGTH) }, hours: zeros(daysInYear(year)) };
}

/** Pads / trims a (possibly loaded) worksheet so every array has its nominal length. */
export function normalizeWorksheet(ws: TimeshareWorksheet): TimeshareWorksheet {
  const fit = (a: number[] | undefined, n: number): number[] => {
    const out = zeros(n);
    (a ?? []).slice(0, n).forEach((v, i) => { out[i] = clampHours(v); });
    return out;
  };
  return {
    ...ws,
    pattern: { alternate: ws.pattern?.alternate ?? false, week1: fit(ws.pattern?.week1, PATTERN_LENGTH), week2: fit(ws.pattern?.week2, PATTERN_LENGTH) },
    hours: fit(ws.hours, daysInYear(ws.year)),
  };
}

export const clampHours = (h: number): number => (Number.isFinite(h) ? Math.max(0, Math.min(HOURS_PER_DAY, h)) : 0);

/** Snap to the nearest Santa Clara step. */
export function snapToStep(h: number): number {
  return SC_STEPS.reduce((best, s) => (Math.abs(s - h) < Math.abs(best - h) ? s : best), 0);
}
/** Next Santa Clara step (wraps from All back to none). */
export function cycleStep(h: number): number {
  const i = SC_STEPS.indexOf(snapToStep(h));
  return SC_STEPS[(i + 1) % SC_STEPS.length] ?? 0;
}

// ---------------------------------------------------------------- weekly pattern

/** Hours flagged on `day` (0 = Monday) of a weekly pattern. */
export const patternDayHours = (week: number[], day: number): number => {
  let n = 0;
  for (let h = 0; h < HOURS_PER_DAY; h++) if (week[day * HOURS_PER_DAY + h]) n++;
  return n;
};
export const patternHour = (week: number[], day: number, hour: number): boolean => (week[day * HOURS_PER_DAY + hour] ?? 0) > 0;
export const patternQuarter = (week: number[], day: number, quarter: number): boolean => patternHour(week, day, quarter * 6);

export function setPatternHour(week: number[], day: number, hour: number, on: boolean): number[] {
  const out = week.slice();
  out[day * HOURS_PER_DAY + hour] = on ? 1 : 0;
  return out;
}
/** Santa Clara mode: a quarter of a day = six consecutive hour flags. */
export function setPatternQuarter(week: number[], day: number, quarter: number, on: boolean): number[] {
  const out = week.slice();
  for (let h = quarter * 6; h < quarter * 6 + 6; h++) out[day * HOURS_PER_DAY + h] = on ? 1 : 0;
  return out;
}

/** Fill the year from the weekly pattern: Year mode repeats week1 by weekday; alternate mode uses week1 on odd ISO weeks and week2 on even ones. */
export function applyPattern(ws: TimeshareWorksheet): TimeshareWorksheet {
  const n = daysInYear(ws.year);
  const hours = zeros(n);
  const start = Date.UTC(ws.year, 0, 1);
  for (let d = 0; d < n; d++) {
    const date = new Date(start + d * 86_400_000);
    const wd = mondayWeekday(date);
    const week = ws.pattern.alternate && isoWeek(date) % 2 === 0 ? ws.pattern.week2 : ws.pattern.week1;
    hours[d] = patternDayHours(week, wd);
  }
  return { ...ws, hours };
}

export function setDayHours(ws: TimeshareWorksheet, dayIndex: number, hours: number): TimeshareWorksheet {
  const out = ws.hours.slice();
  if (dayIndex >= 0 && dayIndex < out.length) out[dayIndex] = ws.mode === 'SANTA_CLARA' ? snapToStep(clampHours(hours)) : clampHours(hours);
  return { ...ws, hours: out };
}

export function eraseWorksheet(ws: TimeshareWorksheet): TimeshareWorksheet {
  return { ...ws, pattern: { alternate: ws.pattern.alternate, week1: zeros(PATTERN_LENGTH), week2: zeros(PATTERN_LENGTH) }, hours: zeros(daysInYear(ws.year)) };
}

// ---------------------------------------------------------------- results

export const totalHours = (ws: TimeshareWorksheet): number => ws.hours.reduce((a, h) => a + h, 0);

/** Percent (0..100) of the year the children spend with ws.parent. */
export function annualPercent(ws: TimeshareWorksheet): number {
  return (totalHours(ws) / (daysInYear(ws.year) * HOURS_PER_DAY)) * 100;
}

/** Percent (0..100) of `month` (0..11) spent with ws.parent. */
export function monthPercent(ws: TimeshareWorksheet, month: number): number {
  const first = dayOfYear(ws.year, month, 1);
  const days = daysInMonth(ws.year, month);
  let sum = 0;
  for (let d = first; d < first + days; d++) sum += ws.hours[d] ?? 0;
  return (sum / (days * HOURS_PER_DAY)) * 100;
}

/** Hours of day `d` with `parent` (the complement when the worksheet tracks the other parent). */
export function hoursWithParent(ws: TimeshareWorksheet, dayIndex: number, parent: PartyIndex): number {
  const h = ws.hours[dayIndex] ?? 0;
  return parent === ws.parent ? h : HOURS_PER_DAY - h;
}

/** Fraction (0..1) of time with the NON-custodial parent implied by the worksheet. */
export function ncpFractionFromWorksheet(ws: TimeshareWorksheet, child: Pick<Child, 'custodialParent'>): number {
  const f = annualPercent(ws) / 100;
  return Math.max(0, Math.min(1, ws.parent === child.custodialParent ? 1 - f : f));
}

/** Hours per year that make up one whole year in the engine's unit table (730 h/month × 12). */
export const HOURS_PER_YEAR_UNIT = UNIT_MONTHS.YEAR / UNIT_MONTHS.HOUR;

/** Attach: the child's timeshare follows the worksheet (units = NCP hours per year). Mutates `child`. */
export function attachWorksheet(child: Child, ws: TimeshareWorksheet): void {
  child.worksheet = ws;
  child.timeshareSource = 'WORKSHEET';
  child.tableKey = 'x';
  const units = Math.round(ncpFractionFromWorksheet(ws, child) * HOURS_PER_YEAR_UNIT * 100) / 100;
  child.timeshare = { units, unitType: 'HOUR', baseUnit: 'YEAR' };
}

/** Detach: keep the worksheet on the child but stop deriving the timeshare from it. */
export function detachWorksheet(child: Child): void {
  if (child.timeshareSource === 'WORKSHEET') child.timeshareSource = 'DIRECT';
}

export const isAttached = (child: Child): boolean => child.timeshareSource === 'WORKSHEET' && child.worksheet !== undefined;

/** The worksheet to edit for a child: its own, or a fresh one tracking the non-custodial parent. */
export function worksheetFor(child: Child, year: number, mode: TimeshareMode): TimeshareWorksheet {
  return child.worksheet ? normalizeWorksheet({ ...child.worksheet, mode }) : emptyWorksheet(year, mode, otherParty(child.custodialParent));
}

/** Calendar cells of a month, Monday-first, padded with null before the 1st. */
export function monthCells(year: number, month: number): (number | null)[] {
  const lead = mondayWeekday(new Date(Date.UTC(year, month, 1)));
  const cells: (number | null)[] = new Array<null>(lead).fill(null);
  for (let d = 1; d <= daysInMonth(year, month); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
